/**
 * Referral Triage Dashboard
 * Hospital receptionist interface for managing incoming referrals
 * Features: Queue view, status updates, patient creation, provider management
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Users, Clock, CheckCircle, AlertTriangle, Search, Filter,
  RefreshCw, ChevronRight, Phone, MapPin, FileText, UserPlus,
  ArrowRight, XCircle, Bell, Eye, QrCode, Building2, TrendingUp,
  Calendar, Stethoscope, Activity, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== STATUS UPDATE MODAL ====================
const StatusUpdateModal = ({ referral, onClose, onUpdate }) => {
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const statusTransitions = {
    pending: [
      { value: 'received', label: 'Mark as Received', color: 'blue' },
      { value: 'cancelled', label: 'Cancel Referral', color: 'red' }
    ],
    received: [
      { value: 'patient_arrived', label: 'Patient Arrived', color: 'purple' },
      { value: 'cancelled', label: 'Cancel Referral', color: 'red' }
    ],
    patient_arrived: [
      { value: 'admitted', label: 'Admit Patient', color: 'green' },
      { value: 'completed', label: 'Mark Completed', color: 'gray' },
      { value: 'cancelled', label: 'Cancel', color: 'red' }
    ],
    admitted: [
      { value: 'completed', label: 'Mark Completed', color: 'gray' }
    ]
  };

  const availableTransitions = statusTransitions[referral.status] || [];

  const handleUpdate = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/referral-triage/referrals/${referral.id}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_status: newStatus,
            notes: notes || null
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update status');
      }

      toast.success(data.message);
      if (data.notification_sent) {
        toast.info('Provider has been notified of this update');
      }
      onUpdate(data.referral);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <h3 className="text-lg font-bold text-gray-900">Update Referral Status</h3>
          <p className="text-sm text-gray-500">{referral.ref_id} - {referral.patient_name}</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Status: <Badge className="ml-2">{referral.status.replace('_', ' ')}</Badge>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
            <div className="space-y-2">
              {availableTransitions.map((transition) => (
                <button
                  key={transition.value}
                  onClick={() => setNewStatus(transition.value)}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    newStatus === transition.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium">{transition.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status change"
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={loading || !newStatus}
              className="flex-1"
              data-testid="confirm-status-update"
            >
              {loading ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== REFERRAL DETAIL MODAL ====================
const ReferralDetailModal = ({ referral, onClose, onStatusUpdate, onCreatePatient }) => {
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const handleCreatePatient = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/referral-triage/referrals/${referral.id}/create-patient`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create patient');
      }

      toast.success(data.message);
      onCreatePatient(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      routine: { color: 'bg-gray-100 text-gray-800', label: 'Routine' },
      urgent: { color: 'bg-orange-100 text-orange-800', label: 'Urgent' },
      emergency: { color: 'bg-red-100 text-red-800', label: 'Emergency' }
    };
    const c = config[priority] || config.routine;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      received: { color: 'bg-blue-100 text-blue-800', label: 'Received' },
      patient_arrived: { color: 'bg-purple-100 text-purple-800', label: 'Patient Arrived' },
      admitted: { color: 'bg-green-100 text-green-800', label: 'Admitted' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl font-bold">{referral.ref_id}</span>
                  {getPriorityBadge(referral.priority)}
                </div>
                <p className="text-blue-100">
                  Referred by {referral.provider_name} ({referral.provider_role})
                </p>
              </div>
              <div className="text-right">
                {getStatusBadge(referral.status)}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Patient Info */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Patient Information
              </h4>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                <div>
                  <span className="text-sm text-gray-500">Name</span>
                  <p className="font-medium text-gray-900">{referral.patient_name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Mobile</span>
                  <p className="font-medium text-gray-900">{referral.patient_mobile}</p>
                </div>
                {referral.patient_age && (
                  <div>
                    <span className="text-sm text-gray-500">Age</span>
                    <p className="font-medium text-gray-900">{referral.patient_age} years</p>
                  </div>
                )}
                {referral.patient_gender && (
                  <div>
                    <span className="text-sm text-gray-500">Gender</span>
                    <p className="font-medium text-gray-900 capitalize">{referral.patient_gender}</p>
                  </div>
                )}
                {referral.village && (
                  <div>
                    <span className="text-sm text-gray-500">Village/Area</span>
                    <p className="font-medium text-gray-900">{referral.village}</p>
                  </div>
                )}
                {referral.patient_address && (
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Address</span>
                    <p className="font-medium text-gray-900">{referral.patient_address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Referral Details */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Referral Details
              </h4>
              <div className="space-y-3">
                {referral.chief_complaint && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <span className="text-sm text-blue-600 font-medium">Chief Complaint</span>
                    <p className="text-gray-900 mt-1">{referral.chief_complaint}</p>
                  </div>
                )}
                {(referral.reason_translated || referral.reason_original) && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <span className="text-sm text-gray-600 font-medium">
                      Reason for Referral
                      {referral.translation_tag && (
                        <Badge variant="outline" className="ml-2 text-xs">{referral.translation_tag}</Badge>
                      )}
                    </span>
                    <p className="text-gray-900 mt-1">{referral.reason_translated || referral.reason_original}</p>
                  </div>
                )}
                {referral.notes && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <span className="text-sm text-gray-600 font-medium">Additional Notes</span>
                    <p className="text-gray-900 mt-1">{referral.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code */}
            {referral.qr_code_data && (
              <div className="mb-6 text-center">
                <h4 className="font-semibold text-gray-900 mb-3">QR Code</h4>
                <div className="inline-block bg-white p-4 rounded-xl border">
                  <img
                    src={`data:image/png;base64,${referral.qr_code_data}`}
                    alt="QR Code"
                    className="w-32 h-32"
                  />
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="text-sm text-gray-500 space-y-1">
              <p>Created: {new Date(referral.created_at).toLocaleString()}</p>
              {referral.received_at && <p>Received: {new Date(referral.received_at).toLocaleString()}</p>}
              {referral.arrived_at && <p>Arrived: {new Date(referral.arrived_at).toLocaleString()}</p>}
              {referral.admitted_at && <p>Admitted: {new Date(referral.admitted_at).toLocaleString()}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 border-t bg-gray-50 flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Close
            </Button>
            {!referral.linked_patient_id && referral.status !== 'cancelled' && (
              <Button
                onClick={handleCreatePatient}
                disabled={loading}
                variant="outline"
                className="flex-1"
                data-testid="create-patient-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {loading ? 'Creating...' : 'Create Patient'}
              </Button>
            )}
            {referral.status !== 'completed' && referral.status !== 'cancelled' && (
              <Button
                onClick={() => setShowStatusModal(true)}
                className="flex-1"
                data-testid="update-status-btn"
              >
                Update Status
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {showStatusModal && (
          <StatusUpdateModal
            referral={referral}
            onClose={() => setShowStatusModal(false)}
            onUpdate={(updated) => {
              onStatusUpdate(updated);
              setShowStatusModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== MAIN TRIAGE DASHBOARD ====================
const ReferralTriageDashboard = () => {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    search: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Build query params
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);

      const [statsRes, queueRes] = await Promise.all([
        fetch(`${API_URL}/api/referral-triage/queue/stats`, { headers }),
        fetch(`${API_URL}/api/referral-triage/queue?${params.toString()}`, { headers })
      ]);

      if (!statsRes.ok || !queueRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const statsData = await statsRes.json();
      const queueData = await queueRes.json();

      setStats(statsData);
      setQueue(queueData.queue || []);
    } catch (error) {
      toast.error('Failed to load triage queue');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleStatusUpdate = (updatedReferral) => {
    setQueue(prev => prev.map(r => r.id === updatedReferral.id ? updatedReferral : r));
    fetchData(); // Refresh stats
  };

  const handlePatientCreated = () => {
    fetchData();
    setSelectedReferral(null);
  };

  const viewReferralDetail = async (referralId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/referral-triage/referrals/${referralId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to load referral');

      const data = await response.json();
      setSelectedReferral(data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'emergency':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'urgent':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'border-l-yellow-500 bg-yellow-50/50',
      received: 'border-l-blue-500 bg-blue-50/50',
      patient_arrived: 'border-l-purple-500 bg-purple-50/50',
      admitted: 'border-l-green-500 bg-green-50/50',
      completed: 'border-l-gray-400 bg-gray-50/50',
      cancelled: 'border-l-red-500 bg-red-50/50'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6" data-testid="referral-triage-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referral Triage</h1>
            <p className="text-gray-500">Manage incoming patient referrals</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-3xl font-bold text-yellow-800">{stats?.total_pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Received</p>
                  <p className="text-3xl font-bold text-blue-800">{stats?.total_received || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">Arrived</p>
                  <p className="text-3xl font-bold text-purple-800">{stats?.total_arrived || 0}</p>
                </div>
                <UserCheck className="w-8 h-8 text-purple-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Urgent</p>
                  <p className="text-3xl font-bold text-red-800">{stats?.urgent_count || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Today</p>
                  <p className="text-3xl font-bold text-green-800">{stats?.today_count || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, phone, or REF-ID..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    data-testid="search-referrals"
                  />
                </div>
              </div>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="patient_arrived">Arrived</SelectItem>
                  <SelectItem value="admitted">Admitted</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.priority}
                onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Queue */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Incoming Referrals Queue
              <Badge variant="outline" className="ml-2">{queue.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {queue.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No referrals found</h3>
                <p className="text-gray-500">Incoming referrals will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {queue.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-xl border-l-4 cursor-pointer hover:shadow-md transition-all ${getStatusColor(item.status)}`}
                    onClick={() => viewReferralDetail(item.id)}
                    data-testid={`queue-item-${item.ref_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center">
                          {getPriorityIcon(item.priority) || <Users className="w-5 h-5 text-gray-400" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{item.patient_name}</span>
                            <Badge variant="outline" className="font-mono">{item.ref_id}</Badge>
                            {item.is_urgent && (
                              <Badge className="bg-red-100 text-red-800">URGENT</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {item.patient_mobile}
                            </span>
                            {item.village && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {item.village}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Stethoscope className="w-3 h-3" /> {item.provider_name}
                            </span>
                          </div>
                          {item.chief_complaint && (
                            <p className="text-sm text-gray-700 mt-1">
                              <span className="font-medium">Complaint:</span> {item.chief_complaint}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          item.status === 'received' ? 'bg-blue-100 text-blue-800' :
                          item.status === 'patient_arrived' ? 'bg-purple-100 text-purple-800' :
                          item.status === 'admitted' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {item.status.replace('_', ' ')}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedReferral && (
          <ReferralDetailModal
            referral={selectedReferral}
            onClose={() => setSelectedReferral(null)}
            onStatusUpdate={handleStatusUpdate}
            onCreatePatient={handlePatientCreated}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ReferralTriageDashboard;
